import React, { useEffect, useState } from 'react';

const STORAGE_KEY = 'pwa_install_dismissed';
const SNOOZE_DAYS = 3;
const FOREVER_DAYS = 30; // "Don't show again" on iOS = 30 days (can't detect uninstall)

function isInstalled() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function isSnoozed() {
  const until = localStorage.getItem(STORAGE_KEY + '_until');
  if (!until) return false;
  return Date.now() < parseInt(until, 10);
}

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Don't show if already running as installed PWA
    if (isInstalled()) return;

    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    if (ios) {
      // iOS can't detect uninstall — respect snooze only
      if (isSnoozed()) return;
      setVisible(true);
      return;
    }

    // For Chrome/Android: beforeinstallprompt only fires when NOT installed.
    // If it fires, the user has either never installed or has uninstalled — always show.
    const handler = (e) => {
      e.preventDefault();
      // Clear any previous forever-dismiss since the app is clearly not installed
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_KEY + '_until');
      setDeferredPrompt(e);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'dismissed') {
      // User declined — snooze for 3 days
      const until = Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000;
      localStorage.setItem(STORAGE_KEY + '_until', String(until));
    }
    setVisible(false);
    setDeferredPrompt(null);
  };

  const handleSnooze = () => {
    const until = Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(STORAGE_KEY + '_until', String(until));
    setVisible(false);
  };

  const handleDismissForever = () => {
    // On iOS we can't detect uninstall, so use a long snooze instead of truly forever
    const days = isIOS ? FOREVER_DAYS : 365;
    const until = Date.now() + days * 24 * 60 * 60 * 1000;
    localStorage.setItem(STORAGE_KEY + '_until', String(until));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        {/* Header */}
        <div className="bg-luxury-navy px-6 pt-6 pb-4 text-center">
          <img
            src="/android-chrome-192x192.png"
            alt="Ifesteimo Transport"
            className="w-28 h-28 mx-auto mb-4 rounded-3xl shadow-lg"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <h2 className="text-white font-bold text-xl font-serif">Install Our App For Luxury Airport Transfer LA</h2>
          <p className="text-luxury-gold text-sm mt-1">Ifesteimo Transport Chauffeur Service</p>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {isIOS ? (
            <div className="text-gray-700 text-sm space-y-3">
              <p className="font-medium text-gray-900">Add to your Home Screen in 2 steps:</p>
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-luxury-navy text-white text-xs font-bold flex items-center justify-center">1</span>
                <p>Tap the <strong>Share</strong> button <span className="inline-block">&#x2BAD;</span> at the bottom of Safari</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-luxury-navy text-white text-xs font-bold flex items-center justify-center">2</span>
                <p>Select <strong>"Add to Home Screen"</strong> from the menu</p>
              </div>
            </div>
          ) : (
            <div className="text-gray-600 text-sm space-y-2">
              <p>Book reliable LAX chauffeur services with luxury rides and professional drivers — right from your home screen.</p>
              <ul className="space-y-1 mt-3">
                {['Instant access from your home screen', 'Faster load times', 'Works without internet'].map(f => (
                  <li key={f} className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 space-y-2">
          {!isIOS && (
            <button
              onClick={handleInstall}
              className="w-full py-3 bg-luxury-navy text-white font-semibold rounded-xl hover:bg-luxury-gold hover:text-luxury-navy transition-colors text-sm"
            >
              Install App
            </button>
          )}
          <button
            onClick={handleSnooze}
            className="w-full py-2.5 border border-gray-200 text-gray-600 font-medium rounded-xl hover:bg-gray-50 transition-colors text-sm"
          >
            Remind me later
          </button>
          <button
            onClick={handleDismissForever}
            className="w-full py-2 text-gray-400 text-xs hover:text-gray-600 transition-colors"
          >
            Don't show again
          </button>
        </div>
      </div>
    </div>
  );
}
